import React, { useState } from 'react'
import { Link } from '@inertiajs/react'
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight } from 'lucide-react'
import DashboardCard from '@/components/dashboard/DashboardCard'
import CompetitorBadge from '@/components/market/CompetitorBadge'
import DemandChip from '@/components/market/DemandChip'
import RangeTrack from '@/components/market/RangeTrack'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatMoney } from '@/lib/format'
import { nightsUntil } from '@/lib/calendar-utils'
import { ABBREV_TOOLTIPS, nightsAway } from '@/lib/plain-language'
import { TONE_STYLE } from '@/lib/market'
import { cn } from '@/lib/utils'
import type { MarketCompetitor, MarketNight, MarketPriceDomain, MarketTone } from '@/types/market'

const INITIAL_ROWS = 14

/**
 * NIGHT BY NIGHT: the rate grid, every competitor's list price for every listed night. It is also
 * the chart's complete text equivalent. Desktop only; on a phone the night rows and the sheet carry
 * the same facts.
 */
export default function NightGrid({
  nights,
  competitors,
  numbers,
  attention,
  currency,
  domain,
  today,
  selectedDate,
  onSelect,
}: {
  nights: MarketNight[]
  competitors: MarketCompetitor[]
  numbers: Map<number, number>
  attention: Map<string, MarketTone>
  currency: string
  domain: MarketPriceDomain
  today: string
  selectedDate: string | null
  onSelect: (date: string) => void
}) {
  const [showAll, setShowAll] = useState(false)
  const shown = showAll ? nights : nights.slice(0, INITIAL_ROWS)
  const money = (v: number) => formatMoney(v, currency)

  return (
    <DashboardCard
      title="Night by night"
      description="Every competitor's list price for each night"
      padded={false}
      footer={
        nights.length > INITIAL_ROWS ? (
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="inline-flex cursor-pointer items-center gap-1 rounded text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            {showAll ? 'Show fewer nights' : `Show all ${nights.length} nights`}
            <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', showAll && 'rotate-180')} />
          </button>
        ) : undefined
      }
    >
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="sticky left-0 z-10 bg-white">Night</TableHead>
              <TableHead>Market</TableHead>
              <TableHead className="text-right">
                <abbr title={ABBREV_TOOLTIPS.OTB} className="cursor-help no-underline">
                  OTB
                </abbr>
              </TableHead>
              <TableHead className="whitespace-nowrap text-right">Priced at</TableHead>
              <TableHead className="min-w-[150px]">Position</TableHead>
              <TableHead>Gap</TableHead>
              {competitors.map((c) => (
                <TableHead key={c.marketPropertyId} className="min-w-[110px] align-bottom">
                  <div className="flex items-start gap-1.5 py-1.5">
                    <CompetitorBadge number={numbers.get(c.marketPropertyId)} className="mt-px" />
                    <div className="min-w-0">
                      <p className="line-clamp-2 text-xs font-medium leading-tight text-foreground" title={c.name}>
                        {c.name}
                      </p>
                      <p className="mt-0.5 text-[10px] font-normal text-muted-foreground">
                        {c.starsLabel}
                        {c.reviewScore != null && ` · ${c.reviewScore}`}
                      </p>
                    </div>
                  </div>
                </TableHead>
              ))}
              <TableHead className="w-10">
                <span className="sr-only">Open prices</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.map((night) => {
              const tone = attention.get(night.date)
              const selected = night.date === selectedDate
              const cellsById = new Map(night.cells.map((c) => [c.marketPropertyId, c]))
              return (
                <TableRow
                  key={night.date}
                  className={cn(
                    selected ? 'bg-primary/5 hover:bg-primary/[0.07]' : night.isWeekend && 'bg-slate-50/60'
                  )}
                  aria-selected={selected}
                >
                  <TableCell
                    className={cn(
                      'sticky left-0 z-10 p-0',
                      selected ? 'bg-teal-50' : night.isWeekend ? 'bg-slate-50' : 'bg-white'
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => onSelect(night.date)}
                      className="relative flex w-full cursor-pointer flex-col items-start px-4 py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40"
                    >
                      {tone && (
                        <span
                          aria-hidden="true"
                          className={cn('absolute inset-y-0 left-0 w-[3px]', TONE_STYLE[tone].accent)}
                        />
                      )}
                      <span
                        className={cn(
                          'whitespace-nowrap text-sm font-medium',
                          selected ? 'text-primary' : 'text-foreground'
                        )}
                      >
                        {night.isToday ? `Tonight, ${night.label}` : night.label}
                      </span>
                      {!night.isToday && (
                        <span className="text-[11px] text-muted-foreground">
                          {nightsAway(nightsUntil(night.date, today))}
                        </span>
                      )}
                    </button>
                  </TableCell>
                  <TableCell>
                    {night.marketDemand === 'unknown' ? (
                      <span className="text-xs text-muted-foreground" title={night.marketDemandLabel}>
                        --
                      </span>
                    ) : (
                      <DemandChip demand={night.marketDemand} label={night.marketDemandLabel} />
                    )}
                  </TableCell>
                  <TableCell
                    className="text-right text-sm tabular-nums text-muted-foreground"
                    title={night.occupancyLabel ?? undefined}
                  >
                    {night.occupancyPct == null ? '--' : `${night.occupancyPct}%`}
                  </TableCell>
                  <TableCell className="text-right text-sm font-semibold tabular-nums text-primary">
                    {night.yourPrice == null ? (
                      <span className="text-xs font-normal text-muted-foreground">Not priced</span>
                    ) : (
                      money(night.yourPrice)
                    )}
                  </TableCell>
                  <TableCell>
                    <RangeTrack
                      min={night.compsetMin}
                      median={night.compsetMedian}
                      max={night.compsetMax}
                      price={night.yourPrice}
                      domain={domain}
                    />
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm text-foreground">{night.gapLabel ?? '--'}</TableCell>
                  {competitors.map((c) => {
                    const cell = cellsById.get(c.marketPropertyId)
                    return (
                      <TableCell key={c.marketPropertyId} className="text-sm" title={cell?.changeLabel ?? undefined}>
                        {cell?.price != null ? (
                          <span className="inline-flex items-center gap-1 font-medium tabular-nums text-foreground">
                            {money(cell.price)}
                            {cell.direction === 'up' && (
                              <ArrowUp aria-label="Up" className="h-2.5 w-2.5 text-emerald-600" />
                            )}
                            {cell.direction === 'down' && (
                              <ArrowDown aria-label="Down" className="h-2.5 w-2.5 text-rose-600" />
                            )}
                          </span>
                        ) : cell?.soldOut ? (
                          <span className="text-xs font-medium text-slate-600">Sold out</span>
                        ) : (
                          <span className="text-xs italic text-muted-foreground">Not checked</span>
                        )}
                        {cell?.minStayLabel && (
                          <span className="mt-0.5 block w-fit rounded-full bg-white px-1.5 py-px text-[10px] font-semibold text-slate-700 ring-1 ring-inset ring-slate-300">
                            {cell.minStayLabel}
                          </span>
                        )}
                      </TableCell>
                    )
                  })}
                  <TableCell className="p-0 pr-2">
                    <Link
                      href={night.pricesHref}
                      aria-label={`Open prices for ${night.label}`}
                      className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Link>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </DashboardCard>
  )
}
